import {AmbientLight, Color, DirectionalLight, PerspectiveCamera, Scene, Vector3, WebGLRenderer} from 'three'

import type {Mesh, Material, BufferGeometry, BaseEvent} from 'three'
import {DEFAULT_CONTAINER_HEIGHT, RENDER_FORMATS, STATUS_TYPES} from '../../render/constants'
import Status from '../../render/status'
import Controls from './controls'
import Doctor from '../../render/doctor'
import {RenderEventDispatcher} from './event-dispatcher'
import Renderable from './renderable'
import {STLParser} from './parsers/stl-parser'
import {logger} from '../../render/setup-globals'

import {assertHTMLElement, invariant} from '../../render/utils'
import {NullRenderer} from './null-renderer'
import {createGridMesh} from './grid-mesh'
import {getRenderableParser} from './parsers'

type ViewerOnReady = (error: string | null, viewer: ThreeDViewer) => void
type ThreeDViewerEvent = {
  type: 'added' | 'error' | 'error:fatal' | 'init' | 'load' | 'loaded' | 'resize' | 'tick'
  added: {
    data: Renderable
  }
  error: {
    data?: string
    message?: string
    object?: Renderable
  }
  'error:fatal': {
    data?: string
    message?: string
  }
  init: {
    data: string
  }
  load: {
    data?: string
    content?: BufferGeometry
  }
  loaded: {
    object?: Renderable
  }
  resize: {
    data: {width: number; height: number}
  }
  tick: {
    data: number
  }
}

class ThreeDViewer extends RenderEventDispatcher<ThreeDViewerEvent> {
  camera: PerspectiveCamera
  cameraFOV = 50
  container: HTMLElement
  controls: Controls | undefined
  doctor: Doctor
  grid: Mesh | undefined
  light: DirectionalLight = new DirectionalLight(0xffffff, 4)
  scene: Scene = new Scene()
  // Used for communicating with the parent window via postMessage
  status: Status = new Status(RENDER_FORMATS.solid)
  objects: {[key: string]: Renderable}
  renderer: WebGLRenderer | NullRenderer
  running = false

  //# External API
  constructor(selector: string, onReady: ViewerOnReady) {
    super()

    this.draw = this.draw.bind(this)
    this.load = this.load.bind(this)
    this.destructor = this.destructor.bind(this)
    this.width = this.width.bind(this)
    this.height = this.height.bind(this)
    this.resize = this.resize.bind(this)
    this.watchdog = this.watchdog.bind(this)
    this.init = this.init.bind(this)
    this.objectLoadHandler = this.objectLoadHandler.bind(this)
    this.updateLight = this.updateLight.bind(this)
    this.updateGrid = this.updateGrid.bind(this)

    const node = document.querySelector(selector)

    invariant(assertHTMLElement(node))

    this.container = node

    // Used for notifing the user of lack of WebGL
    this.doctor = new Doctor(selector)
    // Handle object loading
    this.objects = {}
    this.renderer = this.getRenderer()
    this.camera = new PerspectiveCamera(this.cameraFOV, this.width() / this.height(), 0.01, 10000)

    if (this.container.getAttribute('data-bound') === 'true') {
      return
    }

    this.addEventListener('load', this.#onLoad)
    this.addEventListener('loaded', this.objectLoadHandler)

    this.once('loaded', () => this.draw(true))
    // Fire resize once after STL is loaded for final dimensions
    this.once('loaded', this.resize)
    this.once('error', (event?: BaseEvent & ThreeDViewerEvent['error']) => {
      this.status.set(STATUS_TYPES.error, {error: event?.data})
    })

    // Boot the viewer and
    // invoke the ready callback
    window.addEventListener('resize', this.resize)

    this.init((err: string | null, viewer: ThreeDViewer) => {
      this.container.setAttribute('data-bound', 'true')
      onReady(err, viewer)
    })
  }

  #addLightSources() {
    const {x, y, z} = this.camera.position

    this.scene.add(new AmbientLight(0x404040))
    this.light.position.set(x, y, z)
    this.scene.add(this.light)
  }

  #onLoad(event: BaseEvent & ThreeDViewerEvent['load']) {
    const {data} = event
    if (data && ['error', 'error:fatal'].includes(data)) {
      this.status.set(data as STATUS_TYPES)
    } else if (data === 'finish') {
      // We always resize the render container to a static height,
      // so it is safe to post this message following a successful load of
      // the stl file
      this.status.set(STATUS_TYPES.resize, {height: DEFAULT_CONTAINER_HEIGHT})
      this.removeEventListener('load', this.#onLoad)
    }
  }

  draw(start: boolean) {
    const drawLoop = (tick = 0) => {
      this.dispatchEvent({
        type: 'tick',
        data: tick,
      })
      this.updateLight(this.camera.position)
      this.renderer.render(this.scene, this.camera)

      if (this.running) {
        requestAnimationFrame(drawLoop)
      }
    }

    if (!start) {
      this.running = false
      return
    }

    if (start && this.running) {
      return
    }

    this.running = true
    drawLoop()
  }

  addLoaderEvents(loader: RenderEventDispatcher<ThreeDViewerEvent>, opts = {}) {
    loader.addEventListener('load', (event: BaseEvent & ThreeDViewerEvent['load']) => {
      this.dispatchEvent({
        type: 'load',
        data: 'finish',
      })

      if (event.content) {
        this.dispatchEvent({
          type: 'loaded',
          object: new Renderable(event.content, opts),
        })
      }
    })

    loader.addEventListener('error', event => {
      this.dispatchEvent({
        type: 'load',
        data: 'error',
      })
      logger.debug('Load Error:', event)
    })

    loader.addEventListener('error:fatal', event => {
      this.dispatchEvent({
        type: 'load',
        data: 'error:fatal',
      })
      logger.debug('Load Error:', event)
    })
  }

  loadMarkdown(data: string, options = {}) {
    this.dispatchEvent({
      type: 'load',
      data: 'start',
    })

    const loader = new STLParser()

    this.addLoaderEvents(loader, {...options, side: loader.side()})

    loader.parse(data)
  }

  load(url: string, options = {}) {
    this.dispatchEvent({
      type: 'load',
      data: 'start',
    })

    const loader = getRenderableParser(url)
    this.addLoaderEvents(loader, {...options, side: loader.side()})
    this.status.load(url, {
      json: false,
      success: data => loader.parse(data),
      error: (_error: Error, xhr: Response) => {
        const statusCode = xhr.status
        const type = statusCode === 403 ? 'error:fatal' : 'error'

        this.dispatchEvent({
          type,
          message: `Could not load URL [${url}] [${statusCode}]`,
        })
      },
    })
  }

  //# Internal API
  destructor() {
    // Called when the viewer element is
    // no longer attached to the DOM.
    this.draw(false)
  }

  width() {
    if (window.parent === window) {
      return window.innerWidth
    }

    return this.container.clientWidth || window.clientWidth || 500
  }

  height() {
    if (window.parent === window) {
      return window.innerHeight
    }

    const renderBar = document.querySelector('.js-render-bar')
    const renderBarElHeight = renderBar?.getBoundingClientRect().height ?? 0

    return (this.container.clientHeight || window.clientHeight || 500) - renderBarElHeight
  }

  resize() {
    const [width, height] = [this.width(), this.height()]
    if (!width || !height) {
      return
    } // Avoid a resize to anything with a zero
    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(width, height)
    this.dispatchEvent({
      type: 'resize',
      data: {width, height},
    })

    logger.debug(`Viewport now ${width}x${height}`)
  }

  watchdog() {
    if (this.container) {
      this.destructor()
    }
  }

  init(ready: ViewerOnReady) {
    if (this.container.getAttribute('data-initialized') === 'true') {
      return
    }

    this.once('loaded', () => {
      if (!this.controls) {
        this.controls = new Controls(this)
      }
    })

    this.once('init', () => {
      this.container.setAttribute('data-initialized', 'true')
      this.watchdog()
      this.doctor.updateHealth()
      this.resize()
      ready(null, this)
    })

    if (!this.renderer) {
      return this.dispatchEvent({
        type: 'error',
        data: 'WebGL support is required for this feature.',
      })
    }

    this.renderer.setSize(this.width(), this.height())
    this.container.appendChild(this.renderer.domElement)

    this.scene.background = new Color(0xffffff)
    this.scene.up.set(0, 0, 1)
    this.camera.up.set(0, 0, 1)
    this.scene.add(this.camera)

    // Must come after the renderer's domElement is added to the container,
    /// and the camera is added to the scene.
    this.#addLightSources()

    this.updateGrid()

    this.dispatchEvent({
      type: 'init',
      data: 'finished',
    })
  }

  objectLoadHandler(data: ThreeDViewerEvent['loaded']) {
    const object = data?.object
    const name = object?.name

    if (!name || this.objects[name]) {
      return
    }

    this.objects[name] = object

    if (object.info.scale !== 1.0) {
      const direction = object.info.scale > 1.0 ? 'up' : 'down'
      this.doctor.addProblem(`Object has been scaled ${direction} to fit the view`)
    }

    // Make sure that the object fits within our frustum
    const radius = object?.geometry?.boundingSphere?.radius || 1
    const scaledRadius = radius * 4
    if (!(this.camera.far > scaledRadius)) {
      this.camera.far = scaledRadius
    }
    this.camera.updateProjectionMatrix()

    this.updateGrid(2 * radius)

    this.scene.add(object)
    this.dispatchEvent({type: 'added', data: object})
    this.status.set(STATUS_TYPES.ready)

    logger.debug('Loaded:', object)
  }

  //# Utils
  updateLight(cameraPosition: Vector3) {
    const {x, y, z} = cameraPosition
    this.light.position.set(x, y, z)
  }

  updateGrid(size = 1) {
    if (!this.grid) {
      this.grid = createGridMesh(size)
    } else {
      this.grid.geometry.computeBoundingSphere()
      const jsonGridSize = this.grid.geometry.toJSON() as unknown
      const gridSize = (jsonGridSize as {width: number}).width
      const currentSize = this.grid.geometry.boundingSphere?.radius ?? 0
      if (gridSize <= currentSize) {
        return
      }
      this.scene.remove(this.grid)
      this.grid.geometry.dispose()

      this.grid = createGridMesh(size, this.grid.material as Material)
    }

    this.scene.add(this.grid)
  }

  getRenderer() {
    try {
      return new WebGLRenderer({antialias: true})
    } catch (e) {
      logger.debug('Failed to create WebGLRenderer:', e)
      this.doctor.addProblem('WebGL powered hardware support not available')
    }

    return new NullRenderer()
  }
}

export default ThreeDViewer
